import { useMemo, useState } from 'react'
import { AxisBottom, AxisLeft } from '@visx/axis'
import { Group } from '@visx/group'
import { scaleLinear } from '@visx/scale'
import type { ChartCanvasProps, ScatterChartData, ScatterPoint } from './chartTypes'
import { AXIS_TICK_PROPS, chartColor, formatTooltipValue } from './visxPrimitives'
import { TooltipCard } from './TooltipCard'

type ActivePoint = ScatterPoint & { screenX: number; screenY: number }

export const ScatterChart = ({ data, width, height }: ChartCanvasProps<ScatterChartData>) => {
  const margin = { top: 22, right: 22, bottom: 40, left: 46 }
  const [active, setActive] = useState<ActivePoint | null>(null)
  const xMax = Math.max(1, width - margin.left - margin.right)
  const yMax = Math.max(1, height - margin.top - margin.bottom)
  const xDomain = Math.max(1, ...data.points.map((point) => point.x))
  const yDomain = Math.max(1, ...data.points.map((point) => point.y))
  const xScale = useMemo(() => scaleLinear({ domain: [0, xDomain], range: [0, xMax], nice: true }), [xDomain, xMax])
  const yScale = useMemo(() => scaleLinear({ domain: [0, yDomain], range: [yMax, 0], nice: true }), [yDomain, yMax])
  const accent = chartColor(3)

  return (
    <div className="analysisChartCanvas">
      <svg className="analysisChartSvg" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`${data.xLabel} scatter`}>
        <title>{data.xLabel}</title>
        <Group left={margin.left} top={margin.top}>
          <AxisLeft scale={yScale} numTicks={4} stroke="var(--chart-grid)" tickStroke="var(--chart-grid)" tickLabelProps={() => AXIS_TICK_PROPS} label={data.yLabel} labelProps={{ ...AXIS_TICK_PROPS, textAnchor: 'middle' }} />
          <AxisBottom top={yMax} scale={xScale} numTicks={5} stroke="var(--chart-grid)" tickStroke="var(--chart-grid)" tickLabelProps={() => AXIS_TICK_PROPS} label={data.xLabel} labelProps={{ ...AXIS_TICK_PROPS, textAnchor: 'middle', dy: 22 }} />
          {data.points.map((point) => {
            const cx = xScale(point.x)
            const cy = yScale(point.y)
            const isActive = active?.id === point.id
            return (
              <circle
                key={point.id}
                cx={cx}
                cy={cy}
                r={isActive ? 8 : 5}
                fill={accent}
                fillOpacity={isActive ? 0.9 : 0.68}
                stroke="color-mix(in srgb, var(--surface) 72%, transparent)"
                strokeWidth={isActive ? 2 : 1}
                className="analysisChartScatterPoint"
                tabIndex={0}
                role="button"
                aria-label={`${point.label}: ${point.value}${point.secondary ? ` · ${point.secondary}` : ''}`}
                onPointerEnter={() => setActive({ ...point, screenX: cx + margin.left, screenY: cy + margin.top })}
                onPointerLeave={() => setActive(null)}
                onFocus={() => setActive({ ...point, screenX: cx + margin.left, screenY: cy + margin.top })}
                onBlur={() => setActive(null)}
              />
            )
          })}
        </Group>
      </svg>
      {active && (
        <TooltipCard
          left={active.screenX}
          top={active.screenY}
          accent={accent}
          title={active.label}
          value={formatTooltipValue(active.value)}
          secondary={active.secondary}
        />
      )}
    </div>
  )
}
